/**
 * The favorite-toggle decision, separated from React state and cookies.
 *
 * Favoriting a residence also favorites its floor plan, which makes
 * un-favoriting genuinely ambiguous: the floor plan might be there because of
 * this residence, because of a different one, or because the counselor picked it
 * on its own. Getting that wrong silently deletes a favorite, so the rules live
 * here where they can be tested directly.
 */

export const MEDIA_TYPES = ['virtual_tour', 'video', 'photo_gallery'];

export const isMediaType = (type) => MEDIA_TYPES.includes(type);

/**
 * Adds or removes a uuid from a type's array within a favorites object.
 *
 * An emptied type is deleted rather than left as [], matching what the cookies
 * have always carried.
 *
 * @param {Object} favorites
 * @param {string} type
 * @param {string} uuid
 * @param {boolean} add
 *
 * @returns {Object} A new favorites object.
 */
export const withFavoriteToggled = (favorites, type, uuid, add) => {
  const next = { ...favorites };
  const currentArray = next[type] || [];

  if (add) {
    if (currentArray.includes(uuid)) {
      return next;
    }
    next[type] = [...currentArray, uuid];
    return next;
  }

  const filteredArray = currentArray.filter((favorite) => favorite !== uuid);
  if (filteredArray.length === 0) {
    delete next[type];
  } else {
    next[type] = filteredArray;
  }

  return next;
};

/**
 * Works out everything one favorite toggle changes.
 *
 * @param {Object} state
 * @param {Object} state.favorites
 *   The `favorites` cookie's favorites (place + floor_plan).
 * @param {Object} state.mediaFavorites
 *   The `media_favorites` cookie's favorites.
 * @param {Object} state.residenceFavorites
 *   The `residence_favorites` cookie's favorites.
 * @param {Object} state.autoFloorPlans
 *   `{ [residenceUuid]: floorPlanUuid }` — which residence put which floor plan
 *   in the favorites. A floor plan the counselor favorited directly has no entry.
 * @param {string} type
 * @param {string} uuid
 * @param {string|null} floorPlanUuid
 *   The residence's floor plan, for residence toggles only.
 *
 * @returns {Object}
 *   `nowFavorited`, the bucket that was toggled (`bucket`, one of 'favorites' |
 *   'mediaFavorites' | 'residenceFavorites'), its new value (`toggled`), the new
 *   `autoFloorPlans`, and `floorPlans` — the new `favorites` object when the
 *   residence link changed it, or null when it didn't.
 */
export const resolveFavoriteToggle = ({
  favorites,
  mediaFavorites,
  residenceFavorites,
  autoFloorPlans,
}, type, uuid, floorPlanUuid = null) => {
  const isResidence = type === 'residence';

  let bucket = 'favorites';
  let current = favorites;
  if (isMediaType(type)) {
    bucket = 'mediaFavorites';
    current = mediaFavorites;
  } else if (isResidence) {
    bucket = 'residenceFavorites';
    current = residenceFavorites;
  }

  const wasFavorited = (current[type] || []).includes(uuid);
  const toggled = withFavoriteToggled(current, type, uuid, !wasFavorited);

  const nextAutoFloorPlans = { ...autoFloorPlans };
  let floorPlans = null;

  if (type === 'floor_plan') {
    // Toggling a floor plan directly makes it the counselor's own choice, so
    // drop every residence's claim on it. Without this, un-favoriting one of
    // those residences later would strip a floor plan they deliberately kept.
    Object.keys(nextAutoFloorPlans).forEach((residenceUuid) => {
      if (nextAutoFloorPlans[residenceUuid] === uuid) {
        delete nextAutoFloorPlans[residenceUuid];
      }
    });
  } else if (isResidence && floorPlanUuid) {
    const floorPlanIsFavorited = (favorites.floor_plan || []).includes(floorPlanUuid);

    if (wasFavorited) {
      // Only take the floor plan back if this residence is what put it there
      // and no other favorited residence still shares it.
      const thisResidenceAddedIt = nextAutoFloorPlans[uuid] === floorPlanUuid;
      delete nextAutoFloorPlans[uuid];
      const anotherResidenceClaimsIt = Object.values(nextAutoFloorPlans).includes(floorPlanUuid);

      if (thisResidenceAddedIt && !anotherResidenceClaimsIt && floorPlanIsFavorited) {
        floorPlans = withFavoriteToggled(favorites, 'floor_plan', floorPlanUuid, false);
      }
    } else if (!floorPlanIsFavorited) {
      // Favoriting brought the floor plan along, so record that we did it.
      floorPlans = withFavoriteToggled(favorites, 'floor_plan', floorPlanUuid, true);
      nextAutoFloorPlans[uuid] = floorPlanUuid;
    } else {
      // The floor plan was already favorited on its own. Leave it alone and
      // claim nothing, so un-favoriting this residence won't take it away.
    }
  }

  return {
    bucket,
    toggled,
    floorPlans,
    autoFloorPlans: nextAutoFloorPlans,
    nowFavorited: !wasFavorited,
  };
};

/**
 * Whether a favorite's PATCH to the resident failed, and why.
 *
 * fetchWithAuthentication resolves with the parsed body whatever the status,
 * so a 422 never reaches a .catch — it arrives as a JSON:API `errors` array.
 * Reading only rejections is how a rejected favorite went unreported (#1474).
 *
 * @param {Object|null} response
 *   The resolved body, when the request completed.
 * @param {Error|null} error
 *   The rejection, when it did not.
 *
 * @returns {string|null} The reason to show, or null when the save went through.
 */
export const favoriteSyncFailure = (response, error = null) => {
  if (error) {
    return error.message || 'The request did not complete.';
  }

  const errors = response?.errors;
  if (!Array.isArray(errors) || errors.length === 0) {
    return null;
  }

  return errors[0].detail || errors[0].title || 'The save was rejected.';
};
